import { connectRefinementList } from 'instantsearch.js/es/connectors';
import { createRoot } from 'react-dom/client';
import { createElement } from 'react';
import { setFilterVisibility } from './searchsync-filter-visibility';
import { Checkbox } from '@tangible/ui';
import { getInstance, registerWidget } from './searchsync-create-instance';
import { addFilter } from '@wordpress/hooks';

/**
 * tangible.config.js's `react: 'wp'` build mode (see that file for the full story) rewrites
 * @tangible/ui's prebuilt Checkbox.js's `import { jsx, jsxs } from "react/jsx-runtime"` (it
 * ships compiled with the automatic JSX runtime baked in) into references to a bare
 * `ReactJSXRuntime` global — that's @tangible/roller's hardcoded, non-overridable behavior for
 * this exact specifier in `wp` mode, and WordPress core doesn't define any such global itself.
 * Defined here instead, implemented on top of `wp.element.createElement` (the classic-transform
 * API `@wordpress/element` actually provides). Safe to define unconditionally (idempotent) —
 * this file is the only consumer of @tangible/ui in the bundle, and only needs to exist before
 * Checkbox is first rendered (a React render, which happens later, after an InstantSearch
 * connector's render() callback fires), not before this module merely loads.
 */
if (!window.ReactJSXRuntime) {
  window.ReactJSXRuntime = {
    Fragment: wp.element.Fragment,
    jsx(type, props) {
      return wp.element.createElement(type, props, props && props.children);
    },
    jsxs(type, props) {
      return wp.element.createElement(
        type,
        props,
        ...(Array.isArray(props && props.children) ? props.children : [props && props.children])
      );
    },
  };
}

/**
 * Phase 1c Tangible UI pilot (PLAN.md §2.5 / §2.4): reference implementation for converting an
 * InstantSearch widget from the imperative `instantsearch.js/es/widgets` factory to the
 * connector + Tangible UI rendering pattern.
 *
 * Why a connector (not react-instantsearch-hooks' <InstantSearch>/useConnector): every widget in
 * this plugin shares ONE InstantSearch instance per index, created imperatively by
 * getInstance()/searchsync-create-instance.js and stored on window.tangibleSearchsyncInstances.
 * react-instantsearch-hooks' <InstantSearch> always constructs its OWN InstantSearch.js instance
 * internally (see its useInstantSearchApi() — there is no prop to inject an existing instance),
 * so it cannot compose with getInstance()'s shared-instance/routing/preselection-plan machinery
 * without running two competing InstantSearch.js instances against the same index. The bare
 * `instantsearch.js/es/connectors` connectors (used already by searchsync-clear-refinements-
 * component.js and searchsync-pagination-component.js for custom rendering) have no such
 * constraint: they're just a render callback wired to search.addWidgets(), so they compose fine
 * with the shared instance. This file keeps that same connector-only approach, but replaces the
 * manual per-item DOM string-building with a small React tree (mounted once via
 * ReactDOM.createRoot, re-rendered on each connector render() call) using @tangible/ui's
 * Checkbox component for the actual <input type="checkbox"> + label markup.
 *
 * DOM contract preserved 1:1 with the previous instantsearch.js `refinementList` widget factory
 * (see docs/frontend-architecture.md's per-widget conversion checklist) — same class names on
 * the same elements, so tests/e2e/widgets.spec.js and theme CSS need no changes:
 *   list: 'tgbl-refinement-list'
 *   item: 'tgbl-refinement-list__item tgbl-refinement-item' (+ 'ais-RefinementList-item--selected'
 *     when checked, matching the legacy widget's behavior of toggling that class)
 *   label: 'tgbl-refinement-item__label'
 *   labelText: 'tgbl-refinement-item__text'
 *   checkbox: 'tgbl-refinement-item__checkbox'
 *   count: 'tgbl-refinement-item__count'
 */

// TODO: consolidate this somewhere to be re-used by all the widgets which need it
const transformFacetMappingToDict = (facetMappingObject) => {
  const facetDictionary = {};
  Object.values(facetMappingObject).forEach(facet => {
    facetDictionary[facet.facet_value] = facet.new_facet_value_label;
  });
  return facetDictionary;
};

function RefinementListItem({ item, refine }) {
  const label = item.highlighted ?? item.label;
  return createElement(
    'li',
    {
      className:
        'tgbl-refinement-list__item tgbl-refinement-item' +
        (item.isRefined ? ' ais-RefinementList-item--selected' : ''),
    },
    createElement(
      'label',
      { className: 'tgbl-refinement-item__label' },
      createElement(Checkbox, {
        className: 'tgbl-refinement-item__checkbox',
        checked: item.isRefined,
        onCheckedChange: () => refine(item.value),
        'aria-label': label,
      }),
      createElement('span', { className: 'tgbl-refinement-item__text' }, label),
      createElement('span', { className: 'tgbl-refinement-item__count' }, String(item.count))
    )
  );
}

function RefinementListTree({ items, refine }) {
  return createElement(
    'ul',
    { className: 'tgbl-refinement-list' },
    items.map((item) =>
      createElement(RefinementListItem, { key: item.label, item, refine })
    )
  );
}

function createTangibleUIRefinementList({ container, attribute, limit, sortBy, transformItems }) {
  const root = createRoot(container);

  const render = ({ items, refine }) => {
    // Nothing to refine — the attribute holds no values in this index, or none in these results —
    // so the accordion goes rather than opening onto an empty panel. See
    // searchsync-filter-visibility.js for why an empty list also means nothing is refined.
    setFilterVisibility(container, items.length > 0);
    root.render(createElement(RefinementListTree, { items, refine }));
  };

  const dispose = () => {
    // instantsearch.js connectors call dispose() synchronously during removeWidgets()/instance
    // teardown; deferring unmount avoids "synchronous unmount while React is still rendering"
    // warnings, mirroring the disposal timing react-dom itself recommends for this situation.
    setTimeout(() => root.unmount(), 0);
  };

  const makeWidget = connectRefinementList(render, dispose);

  return makeWidget({ attribute, limit, sortBy, transformItems });
}

function initRefinementListWidget() {

  const tgblAlgoliaDataJSON = window.atob(tgblSearchsyncDataEncoded)
  const tgblAlgoliaData = jQuery.parseJSON(tgblAlgoliaDataJSON)

  // TODO: Translations
  //const LOCALIZED = tgblAlgoliaData.localized;

  // Get all elements with refinement list class
  const elements = document.querySelectorAll("[data-filter-type='refinementList']")
  const elementsArray = Array.from(elements)

  // Group together refinement list elements that share an index
  const elementsGroupedByIndex = elementsArray.reduce((acc, element) => {
    const dataIndexValue = element.getAttribute("data-index");
    if (!acc[dataIndexValue]) {
      acc[dataIndexValue] = [];
    }
    acc[dataIndexValue].push(element);
    return acc;
  }, {});

  // Loop through each index
  for (const dataIndexValue in elementsGroupedByIndex) {
    if (elementsGroupedByIndex.hasOwnProperty(dataIndexValue)) {
      const elementsWithSameIndex = elementsGroupedByIndex[dataIndexValue];
      let search = getInstance(dataIndexValue);

      // Perform actions on elements with the same data-index value
      elementsWithSameIndex.forEach(element => {

        const widgetID = element.getAttribute('data-id')
        const widgetData = tgblAlgoliaData[widgetID]
        const attribute = widgetData.attribute

        let facetMappings = []
        if ( widgetData.facet_mapping ) {
          facetMappings = transformFacetMappingToDict(widgetData.facet_mapping)
        }

        let sortByArgs =  ["isRefined", "count:desc", "name:asc"]

        if ( widgetData.refinement_list_sort ) {
          sortByArgs = Object.values(widgetData.refinement_list_sort)
        }

        // If this widget declares preselected refinements, contribute them via a filter
        if (widgetData.preselected_refinements) {
          const normalizedValues = normalizeRefinements(widgetData.preselected_refinements);
          if (normalizedValues.length > 0) {
            const ns = `tangible/refinementlist/${dataIndexValue}/${widgetID}`;
            addFilter(
              'tangible-algolia-preselect-refinement',
              ns,
              (plan) => {
                plan.push({
                  indexName: dataIndexValue,
                  attribute,
                  values: normalizedValues,
                });
                return plan;
              }
            );
          }
        }

        const widget = createTangibleUIRefinementList({
          container: element,
          attribute,
          limit: 1000,
          sortBy: sortByArgs,
          transformItems(items) {
            return items.map(item => ({
              ...item,
              label: facetMappings[item.label] ?? item.label,
              highlighted: facetMappings[item.label] ?? item.label,
            }));
          },
        });

        search.addWidgets([widget]);
        registerWidget(element);
      });
    }
  }
}

export { initRefinementListWidget }

function normalizeRefinements(input) {
  // Already an array? Use it.
  let arr = Array.isArray(input) ? input : null;

  // If it’s an object (e.g., {0: {...}, 1: {...}}), convert to array
  if (!arr && input && typeof input === 'object') {
    arr = Object.values(input);
  }

  // Single primitive? Wrap
  if (!arr) {
    arr = (input == null) ? [] : [input];
  }

  // Map entries to strings Algolia understands
  return arr
    .map(item => {
      if (item && typeof item === 'object') {
        // Prefer "value", fallback to "label", else JSON string of object
        const candidate = item.value ?? item.label;
        return candidate != null ? String(candidate) : JSON.stringify(item);
      }
      return String(item);
    })
    .filter(v => v.length > 0);
}
